import React from 'react';
import { Button as MuiButton, CircularProgress, Box, useTheme } from '@mui/material';
import { styled } from '@mui/material/styles';

// Styled button component with consistent styling
const StyledButton = styled(MuiButton)(({ theme, size, buttonType }) => ({
  borderRadius: buttonType === 'rounded' ? '24px' : '4px',
  textTransform: 'none',
  fontWeight: 500,
  boxShadow: buttonType === 'floating' ? theme.shadows[2] : 'none',
  backgroundColor: variant => variant === 'contained' ? '#485869' : undefined, // Use top navigation color for contained buttons
  color: variant => variant === 'contained' ? '#FFFFFF' : undefined, // White text for contained buttons
  
  // Size variations
  ...(size === 'large' && {
    padding: '12px 24px',
    fontSize: '1rem',
  }),
  ...(size === 'small' && {
    padding: '4px 12px',
    fontSize: '0.75rem',
  }),
  
  // Hover effect
  '&:hover': {
    boxShadow: buttonType === 'floating' ? theme.shadows[4] : 'none',
    transform: buttonType === 'floating' ? 'translateY(-2px)' : 'none',
    backgroundColor: variant => variant === 'contained' ? '#3A4754' : undefined, // Darker shade on hover
  },
  
  // Transition
  transition: 'all 0.3s ease',
}));

/**
 * Button Component
 * A reusable button component that wraps Material-UI Button with additional features
 * 
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Button text or content
 * @param {string} props.color - Button color (primary, secondary, success, etc.)
 * @param {string} props.variant - Button variant (contained, outlined, text)
 * @param {string} props.size - Button size (small, medium, large)
 * @param {string} props.buttonType - Button type (default, rounded, floating)
 * @param {boolean} props.loading - Whether to show loading state
 * @param {boolean} props.fullWidth - Whether button should take full width
 * @param {Function} props.onClick - Click handler
 * @param {Object} props.sx - Additional styles
 * @returns {React.ReactElement} Styled button component
 */
const Button = ({
  children,
  color = 'primary',
  variant = 'contained',
  size = 'medium',
  buttonType = 'default',
  loading = false,
  fullWidth = false,
  onClick,
  startIcon,
  endIcon,
  sx = {},
  ...rest
}) => {
  const theme = useTheme();
  
  // Determine the content based on loading state
  const buttonContent = loading ? (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      <CircularProgress 
        size={size === 'small' ? 16 : 20} 
        color="inherit" 
        sx={{ mr: children ? 1 : 0 }} 
      />
      {children}
    </Box>
  ) : (
    children
  );
  
  // Calculate start and end icons considering loading state
  const finalStartIcon = loading ? null : startIcon;
  const finalEndIcon = loading ? null : endIcon;
  
  return (
    <StyledButton
      color={color}
      variant={variant}
      size={size}
      buttonType={buttonType}
      fullWidth={fullWidth}
      onClick={loading ? undefined : onClick}
      startIcon={finalStartIcon}
      endIcon={finalEndIcon}
      disabled={loading || rest.disabled}
      sx={sx}
      {...rest}
    >
      {buttonContent}
    </StyledButton>
  );
};

export default Button;